// Transcription marks → reading layers.
//
// Ported from mishnah-viewer's witness-app.js `readRow`, where the meaning of
// each mark was settled by aligning the archive transcriptions with the FJMS
// XML of the same witnesses (whose word classes say it outright). The Bavli
// transcriptions use the same sigla — Hachi Garsinan and the Mishnah archive
// share a transcription convention — so the reader is shared rather than
// copied a second time.
//
// Each letter gets its layer; pure markup (spaces '*', line fillers '<~>',
// dividers, punctuation) is classed `sw-mk` and hidden unless the reader asks
// for the diplomatic transcription; and every root keeps its READING — the
// text after the scribe's and corrector's changes — for comparison.
//
// The analysis is split from the DOM: `analyse` takes plain strings, so the
// rules can be tested in Node, and `readRow` is the thin wrapper that walks
// the text nodes and rewrites them.

export const LAYERS = ['del', 'add', 'rep', 'supra', 'marg', 'rest', 'unc', 'bx'];

export const LAYER_TITLES = {
    del: 'מחוק בכתב היד',
    add: 'תוספת ביד מגיה',
    bx: 'בתעתיק: מילים שאינן בנוסח הבסיס שאליו הותאם התעתיק (בבבלי: דפוס וילנא) — אין זה סימן של תיקון',
    alt: 'קריאה חלופית של המעתיק',
    rep: 'תיקון: נכתב במקום המחוק',
    supra: 'נכתב בין השיטין',
    marg: 'נכתב בגיליון',
    rest: 'השלמת המעתיק',
    unc: 'קריאה לא ודאית',
    note: 'הערת המעתיק',
};

// Elements whose text is never transcription: synopsis labels and sigla, the
// scribe's section numbers, margin references, and text readRow has already
// read. A host adds its own through `opts.skip`.
export const DEFAULT_SKIP = '.ms-break-num, .wit-label, .sw-rd, .sw-sg, .syn-mark';

const HEBL = /[א-ת'"׳״]/;
const MARKUP = /[*~²⟡✍·§:%\/?^.\-0-9()\[\]{}<>⟨⟩]/;

export function escapeHtml(t) {
    return String(t).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]));
}

/**
 * Classify every character of a row.
 *
 * `pieces`: [{text, root}] in reading order — one per text node, `root` the
 * index of the cell/line it belongs to. Delimiters pair across pieces (a
 * deletion may run over a line break: '/(ואכילת' … 'פסחים)/'), which is why a
 * row is analysed whole rather than node by node.
 *
 * Returns `{chars, reads, pre, sigs, how}`, the last four indexed by root:
 *   reads — the reading (deleted letters dropped, corrections kept)
 *   pre   — as first written (deletions kept; supra/margin/over-writing dropped)
 *   sigs  — letters with their layer, so two witnesses with the same reading
 *           but a different scribal history can be told apart
 *   how   — which of del/rep/supra/marg the root holds
 */
export function analyse(pieces, nRoots) {
    if (nRoots == null) nRoots = pieces.reduce((m, p) => Math.max(m, p.root + 1), 0);
    const ch = [];
    pieces.forEach((p, ni) => {
        for (let i = 0; i < p.text.length; i++) ch.push({c: p.text[i], ni});
    });
    const N = ch.length;
    let i, j;

    // Pair delimiters over the whole row. An unpaired delimiter is markup only.
    function pairStack(op, cl) {
        const st = [];
        for (let q = 0; q < N; q++) {
            if (ch[q].c === op) st.push(q);
            else if (ch[q].c === cl && st.length) { const r = st.pop(); ch[r].pair = q; ch[q].pair = r; }
        }
    }
    pairStack('(', ')'); pairStack('[', ']'); pairStack('{', '}'); pairStack('<', '>'); pairStack('⟨', '⟩');

    // A slash between two letters of one word is an alternative reading
    // (אחד/ת, (ם){ן}/(ן){ם}): it stays visible and pairs with nothing. Between
    // two single letters (לפרצדור/ד) they are the two readings of one letter:
    // the reading takes the first, the second is shown with the slash.
    // One whose next slash in the row closes a span at a word's edge opens
    // that span instead (ה/בית הזה/).
    const cand = [];
    let nx = -1;
    for (i = 1; i < N - 1; i++) {
        cand[i] = ch[i].c === '/' && ch[i - 1].ni === ch[i].ni && ch[i + 1].ni === ch[i].ni
            && /[א-ת)}\]]/.test(ch[i - 1].c) && /[א-ת(\[{]/.test(ch[i + 1].c);
    }
    for (i = N - 1; i >= 0; i--) {
        if (ch[i].c !== '/') continue;
        if (cand[i] && (nx < 0 || cand[nx] || !(nx > 0 && ch[nx - 1].ni === ch[nx].ni && ch[nx - 1].c !== ' '
            && !(nx + 1 < N && ch[nx + 1].ni === ch[nx].ni && /[א-ת]/.test(ch[nx + 1].c))))) {
            ch[i].alt = 1;
            if (/[א-ת]/.test(ch[i - 1].c) && /[א-ת]/.test(ch[i + 1].c)) ch[i + 1].altb = 1;
        }
        nx = i;
    }
    function pairSeq(sym, sameNode) {
        let last = -1;
        for (let q = 0; q < N; q++) {
            if (ch[q].c !== sym || ch[q].alt) continue;
            if (last < 0 || (sameNode && ch[last].ni !== ch[q].ni)) { last = q; continue; }
            ch[last].pair = q; ch[q].pair = last; last = -1;
        }
    }
    pairSeq('/', false); pairSeq('?', true); pairSeq('%', false);

    // Brackets round a word written between the lines or in the margin
    // ([כל אדם^], [ו0]) only give the extent of the addition; the letters are
    // the manuscript's, not supplied by the transcriber.
    for (i = 0; i < N; i++) {
        if (ch[i].c === '[' && ch[i].pair !== undefined) {
            for (j = i + 1; j < ch[i].pair; j++) {
                if (ch[j].c === '^' || (ch[j].c === '0' && HEBL.test(ch[j - 1].c))) {
                    ch[i].ext = ch[ch[i].pair].ext = 1; break;
                }
            }
        }
    }

    const d = {'(': 0, '[': 0, '{': 0, '<': 0, '/': 0, '?': 0, '⟨': 0};
    const br = [];
    const CL = {')': '(', ']': '[', '}': '{', '>': '<', '⟩': '⟨'};
    function wordBack(from, ni, flag) {
        for (let k = from; k >= 0 && ch[k].c !== ' ' && ch[k].ni === ni; k--) if (!ch[k].mk) ch[k][flag] = 1;
    }
    for (i = 0; i < N; i++) {
        const o = ch[i], c = o.c;
        if ('([{<⟨'.indexOf(c) >= 0 && o.pair !== undefined) { if (!o.ext) d[c]++; o.mk = 1; if (c === '[') br.push(i); continue; }
        if (CL[c] && o.pair !== undefined) { if (!o.ext) d[CL[c]]--; o.mk = 1; if (c === ']') br.pop(); continue; }
        if (o.alt || o.altb) continue;
        if ((c === '/' || c === '?' || c === '%') && o.pair !== undefined) { if (c !== '%') d[c] += (o.pair > i) ? 1 : -1; o.mk = 1; continue; }
        if (c === '^') {                       // interlinear: its bracket group, or the word before
            o.mk = 1;
            if (br.length) { for (j = br[br.length - 1] + 1; j < i; j++) if (!ch[j].mk) ch[j].supra = 1; }
            else wordBack(i - 1, o.ni, 'supra');
            continue;
        }
        if (c === '0' && i > 0 && HEBL.test(ch[i - 1].c)) { o.mk = 1; wordBack(i - 1, o.ni, 'marg'); continue; }   // margin
        if (d['⟨'] > 0) { o.note = 1; continue; }
        if (c === '.' && ((ch[i - 1] && ch[i - 1].c === '.') || (ch[i + 1] && ch[i + 1].c === '.'))) { o.lac = 1; continue; }
        if (c === '◌') { o.lac = 1; continue; }
        if (MARKUP.test(c)) { o.mk = 1; continue; }
        if (c === ' ') continue;
        // /…/ in the archive transcriptions marks words beyond the text they
        // are keyed to (the Vilna Bavli, where there is one): not a hand, not
        // a correction. The letters are the manuscript's, read as written.
        if (d['('] > 0) o.del = 1;
        if (d['/'] > 0) o.bx = 1;
        if (d['{'] > 0) o.rep = 1;
        if (d['['] > 0 || d['<'] > 0) o.rest = 1;
        if (d['?'] > 0) o.unc = 1;
    }

    const blank = () => Array.from({length: nRoots}, () => '');
    const reads = blank(), sigs = blank(), pres = blank();
    const how = Array.from({length: nRoots}, () => ({}));
    for (const o of ch) {
        const ti = pieces[o.ni].root;
        const letterish = o.c === ' ' || HEBL.test(o.c) || o.lac;
        if (!o.mk && !o.note && !o.del && !o.altb && letterish) reads[ti] += o.lac ? '.' : o.c;
        if (!o.mk && !o.note && !o.altb && !o.rep && !o.supra && !o.marg && letterish) pres[ti] += o.lac ? '.' : o.c;
        if (!o.mk && !o.note && HEBL.test(o.c)) ['del', 'rep', 'supra', 'marg'].forEach(l => { if (o[l]) how[ti][l] = 1; });
        if (!o.mk && !o.note && !o.altb && HEBL.test(o.c)) {
            sigs[ti] += (o.del ? '-' : o.add ? '+' : o.rep ? '=' : o.supra ? '^' : o.marg ? '0' : o.rest ? '[' : o.unc ? '?' : '') + o.c;
        }
    }
    const tidy = r => r.replace(/\s+/g, ' ').trim();
    return {chars: ch, reads: reads.map(tidy), pre: pres.map(tidy), sigs, how};
}

/** The class list for one analysed character. */
export function charClass(o) {
    if (o.alt || o.altb) return 'sw-altr';
    if (o.mk) return 'sw-mk';
    if (o.note) return 'sw-note sw-nref';
    if (o.lac) return 'sw-lac';
    return LAYERS.filter(l => o[l]).map(l => 'sw-' + l).join(' ');
}

/** HTML for piece `ni`: runs of same-layer letters, each run titled by its
 *  first layer so a hover says what the styling means. */
export function renderPiece(chars, ni, titles = LAYER_TITLES) {
    let html = '', run = '', sig = null;
    const flush = () => {
        if (run === '') return;
        if (sig === '') html += escapeHtml(run);
        else {
            const parts = sig.split(' ');
            let t = '';
            LAYERS.concat(['note', 'alt']).forEach(l => { if (!t && parts.indexOf('sw-' + l) >= 0) t = titles[l]; });
            html += '<span class="' + sig + '"' + (t ? ' title="' + t + '"' : '') + '>' + escapeHtml(run) + '</span>';
        }
        run = '';
    };
    for (const o of chars) {
        if (o.ni !== ni) continue;
        const g = charClass(o);
        if (g !== sig) { flush(); sig = g; }
        run += o.c;
    }
    flush();
    return html;
}

/**
 * Read the marks in `roots` (elements: a row's cells, or a column's lines) and
 * rewrite their text nodes as layered spans (`span.sw-rd`).
 *
 * Returns the readings as an array indexed like `roots`, carrying `.sigs`,
 * `.pre` and `.how` — the shape mishnah-viewer's synopsis already consumes.
 */
export function readRow(roots, opts = {}) {
    const skip = opts.skip || DEFAULT_SKIP;
    const titles = opts.titles || LAYER_TITLES;
    const nodes = [];
    roots.forEach((root, ti) => {
        const doc = root.ownerDocument;
        const w = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */, {
            acceptNode(n) {
                const p = n.parentNode;
                return (!p || p.closest(skip)) ? 2 /* FILTER_REJECT */ : 1 /* FILTER_ACCEPT */;
            },
        });
        let n;
        while ((n = w.nextNode())) nodes.push({n, root: ti});
    });
    const a = analyse(nodes.map(o => ({text: o.n.nodeValue, root: o.root})), roots.length);
    nodes.forEach((nd, ni) => {
        const span = nd.n.ownerDocument.createElement('span');
        span.className = 'sw-rd';
        span.innerHTML = renderPiece(a.chars, ni, titles);
        nd.n.parentNode.replaceChild(span, nd.n);
    });
    const out = a.reads.slice();
    out.sigs = a.sigs;
    out.pre = a.pre;
    out.how = a.how;
    return out;
}

/** Legend entries, in the order the witness reader shows them. */
export const LEGEND = [
    ['sw-del', 'מחוק', 'del'],
    ['sw-rep', 'תיקון', 'rep'],
    ['sw-supra', 'בין השיטין', 'supra'],
    ['sw-marg', 'בגיליון', 'marg'],
    ['sw-rest', 'השלמה', 'rest'],
    ['sw-unc', 'ספק', 'unc'],
    ['sw-lac', '...', null],
];

/** Turn long runs of lacuna dots inside `root` into proportional gaps (styled
 *  by marks.css outside the diplomatic view); the count stays in the title. */
export function lacunaGaps(root, min = 4) {
    root.querySelectorAll('.sw-lac, .wt-lac').forEach(e => {
        if (e.classList.contains('mvc-gap') || e.querySelector('.sw-lac')) return;
        const n = (e.textContent.match(/[.◌]/g) || []).length;
        if (n < min) return;
        e.classList.add('mvc-gap');
        e.style.setProperty('--n', Math.min(n, 20));
        e.title = 'כ־' + n + ' אותיות שאינן נקראות';
    });
}

/** The legend as HTML: each layer drawn in its own style. */
export function legendHtml(titles = LAYER_TITLES) {
    return '<div class="mvc-legend mvc-marks">' + LEGEND.map(([cls, txt, key]) =>
        '<span title="' + escapeHtml(key ? titles[key] : 'אותיות שאינן נקראות') + '"><span class="' + cls + '">'
        + escapeHtml(txt) + '</span> <small>' + escapeHtml(key ? titles[key] : 'חסר') + '</small></span>').join('')
        + '</div>';
}
